// TVMode.jsx — wall-mounted TV kiosk view. Renders OUTSIDE the Layout wrapper
// so there's no sidebar or header, big type readable from across the room,
// auto-refreshes every 60s off the existing GET /api/dashboard endpoint (which
// already applies role-visibility filtering server-side, so this page shows
// whatever the logged-in user is entitled to see).
//
// Todd's Feb 2026 ask: fleet status, who's got what machine, decisions needed,
// no chrome, no timeouts. There's no idle-logout logic in the app, so once the
// TV logs in the session persists indefinitely — no defeat needed.
import React, { useEffect, useRef, useState } from "react";
import { api, money0, shortDate } from "../lib/api";

const REFRESH_MS = 60_000;
const CURSOR_IDLE_MS = 5_000;

function useAutoHideCursor() {
  const ref = useRef(null);
  useEffect(() => {
    let t = null;
    const el = ref.current;
    if (!el) return;
    const hide = () => { el.style.cursor = "none"; };
    const show = () => {
      el.style.cursor = "auto";
      if (t) clearTimeout(t);
      t = setTimeout(hide, CURSOR_IDLE_MS);
    };
    show();
    window.addEventListener("mousemove", show);
    window.addEventListener("keydown", show);
    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("mousemove", show);
      window.removeEventListener("keydown", show);
      if (el) el.style.cursor = "auto";
    };
  }, []);
  return ref;
}

function fmtNow() {
  const d = new Date();
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function TVMode() {
  const [dash, setDash] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [err, setErr] = useState(null);
  const rootRef = useAutoHideCursor();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/dashboard");
        if (!alive) return;
        setDash(r.data);
        setRefreshedAt(new Date());
        setErr(null);
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.data?.detail || e.message);
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (err && !dash) {
    return (
      <div style={styles.errorScreen} data-testid="tv-error">
        <div style={{ fontSize: 32, opacity: 0.8, marginBottom: 12 }}>TV mode</div>
        <div style={{ fontSize: 24 }}>Can't load the fleet: {err}</div>
        <div style={{ fontSize: 16, opacity: 0.6, marginTop: 20 }}>Trying again every 60 seconds…</div>
      </div>
    );
  }
  if (!dash) return <div style={styles.errorScreen}>Loading…</div>;

  const k = dash.kpis || {};
  const onHireRows = dash.on_hire_rows || [];
  const comingBack = dash.coming_back || [];
  const decisions = dash.decisions || [];
  const vis = dash.visibility || {};

  return (
    <div ref={rootRef} style={styles.root} data-testid="tv-mode">
      <div style={styles.header}>
        <div style={styles.brand}>
          <img src="/assets/logo-mark.png" alt="" style={{ height: 44 }} />
          <span style={styles.brandText}>All Cranes · Hire</span>
        </div>
        <div style={styles.clock} data-testid="tv-clock">{fmtNow()}</div>
      </div>

      <div style={styles.kpiRow}>
        {vis.kpi_fleet_count !== false && (
          <Kpi testid="tv-kpi-fleet" big={k.fleet_count ?? "—"} label="in the fleet" />
        )}
        {vis.kpi_on_hire_count !== false && (
          <Kpi testid="tv-kpi-onhire" big={k.on_hire_count ?? 0} label="out earning right now" tone="green" />
        )}
        {vis.kpi_total_this_month !== false && (
          <Kpi testid="tv-kpi-thismonth" big={money0(k.total_this_month || 0)} label="charges this month" tone="maroon" />
        )}
        {vis.kpi_money_owed !== false && (
          <Kpi testid="tv-kpi-owed" big={money0(k.money_owed || 0)} label="money owed to us" tone={((k.money_owed || 0) > 0) ? "amber" : "green"} />
        )}
        {vis.kpi_papers !== false && (
          <Kpi testid="tv-kpi-papers" big={k.papers_out_of_date_count ?? 0} label="rego/inspections to sort" tone={((k.papers_out_of_date_count || 0) > 0) ? "red" : "green"} />
        )}
      </div>

      <div style={styles.grid}>
        <div style={styles.card} data-testid="tv-onhire-card">
          <div style={styles.cardTitle}>Out earning right now  ·  {onHireRows.length}</div>
          <div style={{ overflow: "hidden", flex: 1 }}>
            {onHireRows.length === 0 ? (
              <div style={styles.emptyMuted}>Nothing on hire.</div>
            ) : (
              <table style={styles.table}>
                <tbody>
                  {onHireRows.slice(0, 12).map((r) => (
                    <tr key={r.hire_id || r.machine_id}>
                      <td style={styles.tdUnit}>{r.unit_number}</td>
                      <td style={styles.tdCust}>{r.customer_name || "—"}</td>
                      <td style={styles.tdSite}>{r.site_or_notes || ""}</td>
                    </tr>
                  ))}
                  {onHireRows.length > 12 && (
                    <tr><td colSpan={3} style={styles.moreRow}>+ {onHireRows.length - 12} more</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {vis.card_coming_back !== false && (
          <div style={styles.card} data-testid="tv-comingback-card">
            <div style={styles.cardTitle}>Coming back / free soon  ·  {comingBack.length}</div>
            <div style={{ overflow: "hidden", flex: 1 }}>
              {comingBack.length === 0 ? (
                <div style={styles.emptyMuted}>None flagged.</div>
              ) : (
                <table style={styles.table}>
                  <tbody>
                    {comingBack.slice(0, 12).map((r) => (
                      <tr key={r.hire_id || r.machine_id}>
                        <td style={styles.tdUnit}>{r.unit_number}</td>
                        <td style={styles.tdCust}>{r.customer_name || "—"}</td>
                        <td style={styles.tdSite}>{r.expected_end_date ? `back ${shortDate(r.expected_end_date)}` : "in the yard"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {vis.card_decisions !== false && (
          <div style={{ ...styles.card, gridColumn: "1 / -1" }} data-testid="tv-decisions-card">
            <div style={styles.cardTitle}>Needs a decision today  ·  {decisions.length}</div>
            <div style={{ overflow: "hidden", flex: 1 }}>
              {decisions.length === 0 ? (
                <div style={styles.emptyMuted}>Nothing waiting.</div>
              ) : (
                <ul style={styles.decisionList}>
                  {decisions.slice(0, 8).map((d, i) => (
                    <li key={d.id || i} style={styles.decisionItem}>
                      <span style={{ fontWeight: 700, marginRight: 12 }}>{d.title || d.label || "—"}</span>
                      <span style={{ opacity: 0.7 }}>{d.detail || d.subtitle || ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={styles.footer} data-testid="tv-refreshed-at">
        {err ? `⚠ last refresh failed — retrying · ` : ""}
        {refreshedAt ? `refreshed ${refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
        {"  ·  auto-refreshes every minute"}
      </div>
    </div>
  );
}

function Kpi({ big, label, tone = "neutral", testid }) {
  const color = TONE[tone] || TONE.neutral;
  return (
    <div style={{ ...styles.kpi, borderTop: `6px solid ${color}` }} data-testid={testid}>
      <div style={{ ...styles.kpiBig, color }}>{big}</div>
      <div style={styles.kpiLabel}>{label}</div>
    </div>
  );
}

const TONE = {
  neutral: "#0B1F3A",
  green:   "#1f7a3d",
  amber:   "#b45309",
  red:     "#a4243b",
  maroon:  "#7a1a2b",
};

const styles = {
  root: {
    minHeight: "100vh",
    width: "100vw",
    background: "#0f172a",
    color: "#f8fafc",
    fontFamily: "-apple-system, system-ui, sans-serif",
    padding: "28px 40px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: { display: "flex", alignItems: "center", gap: 14 },
  brandText: { fontSize: 28, fontWeight: 700, letterSpacing: 0.4 },
  clock: { fontSize: 32, fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 20,
  },
  kpi: {
    background: "#1e293b",
    borderRadius: 12,
    padding: "20px 24px",
    minHeight: 130,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  kpiBig: { fontSize: 56, fontWeight: 700, lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  kpiLabel: { fontSize: 16, marginTop: 10, opacity: 0.75, letterSpacing: 0.3 },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 20,
    flex: 1,
    minHeight: 0,
  },
  card: {
    background: "#1e293b",
    borderRadius: 12,
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 14,
    letterSpacing: 0.3,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 22 },
  tdUnit: { padding: "10px 12px 10px 0", fontWeight: 700, whiteSpace: "nowrap" },
  tdCust: { padding: "10px 12px", whiteSpace: "nowrap", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" },
  tdSite: { padding: "10px 0 10px 12px", opacity: 0.7, fontSize: 18 },
  moreRow: { padding: "10px 0", opacity: 0.6, fontStyle: "italic", fontSize: 18 },
  emptyMuted: { opacity: 0.5, fontSize: 20, fontStyle: "italic" },
  decisionList: { listStyle: "none", padding: 0, margin: 0, fontSize: 20 },
  decisionItem: { padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  footer: {
    fontSize: 14,
    opacity: 0.55,
    textAlign: "right",
    letterSpacing: 0.3,
  },
  errorScreen: {
    minHeight: "100vh",
    background: "#0f172a",
    color: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "-apple-system, system-ui, sans-serif",
  },
};
